import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  styled,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CloudArrowUpIcon, XIcon } from '@phosphor-icons/react';
import theme from '../../theme';
import ReusableModal from '../common/Modal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveAudioEvaluationApi } from '../../utils/apis';

const UploadIconBox = styled(Box)({
  backgroundColor: theme.palette.custom.sidebarIconBg,
  padding: '8px',
  borderRadius: '6px',
  marginBottom: '8px',
});

const UploadInstructions = styled(Typography)({
  textAlign: 'center',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  color: theme.palette.text.secondary,
});

const UploadButton = styled(Button)({
  color: theme.palette.primary.main,
  textTransform: 'none',
  padding: 0,
  minWidth: 'auto',
});

interface AudioUploadModalProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
}

const AudioUploadModal: React.FC<AudioUploadModalProps> = ({
  open,
  onClose,
  agentId,
}) => {
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [alert, setAlert] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [uploadingIndex, setUploadingIndex] = useState<number>(-1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: uploadFile, isPending } = useMutation({
    mutationFn: saveAudioEvaluationApi,
    onSuccess: () => {
      // Check if there are more files to upload
      const nextIndex = uploadingIndex + 1;
      if (nextIndex < selectedFiles.length) {
        setUploadingIndex(nextIndex);
        uploadFile({
          agentId,
          audioFile: selectedFiles[nextIndex],
        });
      } else {
        // All files uploaded successfully
        queryClient.invalidateQueries({
          queryKey: ['audio-evaluation-files'],
        });
        setAlert({
          type: 'success',
          message: 'Audio files uploaded successfully!',
        });
        setTimeout(() => {
          setAlert(null);
          setSelectedFiles([]);
          setErrorMessage(null);
          setUploadingIndex(-1);
          onClose();
        }, 2000);
      }
    },
    onError: (error: any) => {
      setAlert({
        type: 'error',
        message:
          error?.response?.data?.message ||
          'Failed to upload audio files. Please try again.',
      });
      setUploadingIndex(-1);
    },
  });

  const isValidAudioFile = (file: File): boolean => {
    const validTypes = [
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/m4a',
      'audio/ogg',
      'audio/webm',
    ];
    const validExtensions = ['.mp3', '.wav', '.m4a', '.ogg', '.webm'];

    const hasValidType =
      file.type.startsWith('audio/') || validTypes.includes(file.type);
    const hasValidExtension = validExtensions.some(ext =>
      file.name.toLowerCase().endsWith(ext)
    );

    return hasValidType || hasValidExtension;
  };

  const handleFileUpload = (event: React.MouseEvent) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setErrorMessage(null);
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach(file => {
      if (isValidAudioFile(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setErrorMessage(
        `Invalid file type(s): ${invalidFiles.join(', ')}. Please select audio files only.`
      );
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }

    // Reset input to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadFiles = () => {
    if (selectedFiles.length === 0) {
      setErrorMessage('Please select at least one audio file to upload.');
      return;
    }

    if (!agentId) {
      setErrorMessage('Agent ID is missing. Please select an agent.');
      return;
    }

    setErrorMessage(null);
    setAlert(null);

    // Start uploading the first file
    setUploadingIndex(0);
    uploadFile({
      agentId,
      audioFile: selectedFiles[0],
    });
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    setErrorMessage(null);
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach(file => {
      if (isValidAudioFile(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setErrorMessage(
        `Invalid file type(s): ${invalidFiles.join(', ')}. Please select audio files only.`
      );
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleClose = () => {
    if (!isPending) {
      setSelectedFiles([]);
      setErrorMessage(null);
      setUploadingIndex(-1);
      onClose();
    }
  };

  const getUploadProgress = () => {
    if (uploadingIndex >= 0 && selectedFiles.length > 0) {
      return `Uploading ${uploadingIndex + 1} of ${selectedFiles.length}`;
    }
    return 'Uploading...';
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Upload Audio Files"
      subtitle="Please upload audio files (MP3, WAV, M4A, OGG, etc.)"
      width={500}
    >
      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}
      <Box sx={{ mt: 2 }}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm"
          multiple
          style={{ display: 'none' }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed #DCE0E7',
            borderRadius: '8px',
            p: 3,
            width: '100%',
            cursor: 'pointer',
            backgroundColor: '#FAFBFC',
            '&:hover': {
              backgroundColor: '#F5F6F7',
              borderColor: theme.palette.primary.main,
            },
          }}
          onClick={handleFileUpload}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {selectedFiles.length === 0 ? (
            <>
              <UploadIconBox>
                <CloudArrowUpIcon
                  size={24}
                  color={theme.palette.lightGray.text}
                />
              </UploadIconBox>
              <UploadInstructions variant="body2">
                Only <strong>Audio files</strong> are supported. <br />
                Please upload valid audio files (MP3, WAV, M4A, OGG, etc.)
              </UploadInstructions>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}
              >
                <UploadInstructions variant="body2">
                  Drop files here or
                </UploadInstructions>
                <UploadButton onClick={handleFileUpload}>
                  Browse from device
                </UploadButton>
              </Box>
            </>
          ) : (
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                }}
              >
                {selectedFiles.length} file(s) selected
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mb: 2,
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                {selectedFiles.map((file, index) => (
                  <Box
                    key={`${file.name}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: '#FFF',
                      borderRadius: '6px',
                      border: `1px solid ${theme.palette.lightGray.main}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '14px',
                          color: theme.palette.text.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Chip
                        label={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '12px',
                          backgroundColor: theme.palette.custom.sidebarIconBg,
                          color: theme.palette.custom.textBlue,
                        }}
                      />
                    </Box>
                    <Box
                      onClick={e => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        ml: 1,
                        '&:hover': {
                          opacity: 0.7,
                        },
                      }}
                    >
                      <XIcon size={20} color={theme.palette.error.main} />
                    </Box>
                  </Box>
                ))}
              </Box>
              <Button
                variant="contained"
                onClick={e => {
                  e.stopPropagation();
                  handleUploadFiles();
                }}
                disabled={isPending}
                sx={{
                  textTransform: 'none',
                  width: '100%',
                  height: '40px',
                  borderRadius: '6px',
                }}
                startIcon={
                  isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {isPending
                  ? getUploadProgress()
                  : `Upload ${selectedFiles.length} File(s)`}
              </Button>
            </Box>
          )}
        </Box>

        {errorMessage && (
          <Box
            sx={{
              padding: '12px',
              backgroundColor: '#FFEBEE',
              borderRadius: '6px',
              border: `1px solid ${theme.palette.error.main}`,
              mt: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.error.main,
                fontSize: '14px',
              }}
            >
              {errorMessage}
            </Typography>
          </Box>
        )}
      </Box>
    </ReusableModal>
  );
};

export default AudioUploadModal;
